import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Form, Formik } from 'formik';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

import { useLoader } from '../../provider/LoaderProvider';
import FormFields from './FormFields';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import useUsers from '../../context/UsersContext';
import dayjs from 'dayjs';
import masterDataService from '../../service/masterData.service';
import { cleanValues, generateAutocompleteItems, getJokerFieldType } from '../../utils/helper';
import taskListService from '../../service/taskList.service';
import useMenus from '../../context/MenuContext';
import SimpleTaskListTabs from './SimpleTaskListTabs';
import useParam from '../../context/ParamContext';
import jokerService from '../../service/joker.service';
import { JokerFieldTypes, JokerTypes } from '../../config/joker';

export const SimpleTaskListPage = ({ open = false, data = {}, handleClose = () => {}, setTaskChanged = () => {} }) => {
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId, getSubMenuNamesByUrls } = useMenus();
  const { params, getParam } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    user: [],
    userGroup: [],
    type: [],
    status: [],
    brigade: [],
    resourceOne: [],
    resourceThree: [],
    resourceFour: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [viewField, setViewField] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLoading, setJokerLoading] = useState(true);

  const def = {
    startDate: dayjs(),
    lineId: null,
    name: '',
    duration: 1,
    responsiblePersonId: null,
    userGroupId: null,
    typeId: null,
    statusId: null,
    description: '',
    attachment: [],
    taskListRespPerson: [],
    preparedness: '',
    brigadeId: null,
    taskListResourceOne: [],
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    resourceThreeId: null,
    resourceFourId: null,
  };

  const simpleTaskSubmenuId = getMenuSubMenuId('simpleTask');
  const resourceOneSubmenuId = getMenuSubMenuId('resourceOne');

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    setInitValues(def);
    getMenus();
    getJokerFields();
  }, []);

  useEffect(() => {
    if (Object.keys(data).length > 0 && autocompleteOptions.project.length > 0) {
      const formatted = {
        ...data,
        startDate: dayjs(data.startDate),
        attachment: data.taskListAttachment ?? [],
        taskListResourceOne: data.taskListResourceOne?.map((item) => ({
          label: item.resourceOne.name,
          value: item.resourceOneId,
        })),
      };

      setInitValues(formatted);
      setViewField(true);
    }
  }, [data, autocompleteOptions.project]);

  const showBrigadeManagement = params.TASKLIST_SHOW_BRIGADE_MANAGEMENT === 'Y';
  const showResourceThreeField = params.TASKLIST_SHOW_RESOURCETHREE_FIELD === 'Y';
  const showResourceFourField = params.TASKLIST_SHOW_RESOURCEFOUR_FIELD === 'Y';

  useEffect(() => {
    if (!menus?.length || !Object.keys(params).length) return;

    initDefaultValues();
  }, [simpleTaskSubmenuId, resourceOneSubmenuId, showBrigadeManagement]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0 && !data.id) {
      setInitValues((prev) => ({
        ...prev,
        typeId: autocompleteData?.type?.defaultValue ?? null,
        statusId: autocompleteData?.status?.defaultValue ?? null,
      }));
    }
  }, [autocompleteData, data]);

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.TASKLIST)
      .then((data) => {
        setJokerFields(data);
      })
      .finally(() => {
        setJokerLoading(false);
      });
  };

  const validationSchema = Yup.object().shape({
    lineId: Yup.string().required(),
    startDate: Yup.string().required(),
    name: Yup.string().required(),
    duration: Yup.number().required(),
    responsiblePersonId: Yup.string().nullable(),
    userGroupId: Yup.string().nullable(),
    typeId: Yup.string().required(),
    statusId: Yup.string().required(),
    description: Yup.string().nullable().max(1500),
    preparedness: Yup.number().nullable(),
    brigadeId: Yup.number().nullable(),
    resourceThreeId: Yup.string().nullable(),
    resourceFourId: Yup.string().nullable(),
    taskListResourceOne: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.number().required(),
        })
      )
      .when('brigadeId', {
        is: (brigadeId) => brigadeId != null,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema,
      }),
    ...(jokerFields.length > 0
      ? Object.fromEntries(
          jokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      simpleTaskSubmenuId ? masterDataService.typeToForm(simpleTaskSubmenuId) : Promise.resolve({ typeItems: [] })
    );

    promiseArray.push(
      simpleTaskSubmenuId ? masterDataService.statusToForm(simpleTaskSubmenuId) : Promise.resolve({ statusItems: [] })
    );

    promiseArray.push(
      showBrigadeManagement && resourceOneSubmenuId
        ? masterDataService.statusToForm(resourceOneSubmenuId)
        : Promise.resolve({ statusItems: [] })
    );

    promiseArray.push(showBrigadeManagement ? masterDataService.resourceOneToForm() : Promise.resolve([]));

    promiseArray.push(showResourceThreeField ? masterDataService.resourceThreeToForm() : Promise.resolve([]));

    promiseArray.push(showResourceFourField ? masterDataService.resourceFourToForm() : Promise.resolve([]));

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          user: resp[1],
          userGroup: resp[2],
          type: resp[3],
          status: resp[4],
          brigade: resp[5],
          resourceOne: resp[6],
          resourceThree: resp[7],
          resourceFour: resp[8],
        };
        setAutocompleteData(aData);

        const filteredR1 = aData.resourceOne.filter((r1) => r1.active);
        const filteredR3 = aData.resourceThree.filter((r3) => r3.active);
        const filteredR4 = aData.resourceFour.filter((r4) => r4.active);
        const filteredType = aData.type.typeItems?.filter((type) => type.onTaskListAdHocTask);
        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroup: generateAutocompleteItems(aData.userGroup, 'name', 'id'),
          type: generateAutocompleteItems(filteredType, 'itemName', 'itemId'),
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          brigade: generateAutocompleteItems(aData.brigade.statusItems, 'itemName', 'itemId'),
          resourceOne: generateAutocompleteItems(filteredR1, 'name', 'id'),
          resourceThree: generateAutocompleteItems(filteredR3, 'name', 'id'),
          resourceFour: generateAutocompleteItems(filteredR4, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (values.id) {
      updateObject(values);
    } else {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const foundStatus = autocompleteData.status.statusItems.find((status) => status.itemId === values.statusId);

    const updData = {
      ...cleanValues(values),
      updatedBy: user.userId,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
      duration: parseInt(values.duration),
      preparedness: parseFloat(foundStatus?.preparedness ?? null),
      taskListResourceOne: values.taskListResourceOne.map((item) => item.value),
    };

    updData.existingFiles = updData.attachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.attachment) {
      for (const file of updData.attachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    taskListService.updateTaskList(formData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setTaskChanged(true);
      handleClose();
    });
  };

  const handleInsertObject = (values, resetForm) => {
    showLoader();

    const foundStatus = autocompleteData.status.statusItems.find((status) => status.itemId === values.statusId);

    const insertObject = {
      ...cleanValues(values),
      createdBy: user.userId,
      userId: user.userId,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
      duration: parseInt(values.duration),
      preparedness: parseFloat(foundStatus?.preparedness ?? null),
      taskListResourceOne: values.taskListResourceOne.map((item) => item.value),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.attachment) {
      for (const file of insertObject.attachment) {
        formData.append('files', file);
      }
    }

    taskListService
      .createTaskList(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
        setTaskChanged(true);
        handleClose();
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    taskListService.deleteTaskList(deleteData, data.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setShowDeleteDialog(false);
      setTaskChanged(true);
      handleClose();
    });
  };

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceThree', 'resourceFour']);
  }, [menus]);

  return (
    <div className={`${!open && 'pt-20 fixed top-0 left-0 overflow-auto'} bg-gray-100 h-full w-full`}>
      <div className={`${!open && 'py-5 px-5 md:w-[650px] w-[400px]  mx-auto'} h-auto bg-white rounded-lg`}>
        {Object.keys(data).length > 0 && (
          <FormHeaderUI
            modClick={() => {
              setViewField(false);
            }}
            modDisabled={!viewField}
            deleteClick={() => {
              setShowDeleteDialog(true);
            }}
            deleteDisabled={!viewField}
          />
        )}
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {() => (
            <Form>
              <FormFields
                autocompleteOptions={autocompleteOptions}
                user={user}
                viewField={viewField}
                autocompleteData={autocompleteData}
                submenuNames={submenuNames}
              />
              {!jokerLoading && <SimpleTaskListTabs disabled={viewField} />}

              <FormButtonPanelUI />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
